import React, {
  memo,
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
} from 'react';
import * as Styled from './styled';
import {
  StyledText,
  Input,
  Button,
} from '@leasequery/lq.react-component-library';
import { postArchiveReport } from '../../../api/report/postArchiveReport';
import { DialogWithTitle } from '../../../components';

interface ArchiveDialogProps {
  reportResultId: number;
  isOpen: boolean;
  onStateChange: (isOpen: boolean) => void;
  onArchive?: (taskId: number, archiveName: string) => void;
  archivedReportNames: string[];
}

function ArchiveDialog({
  reportResultId,
  isOpen,
  onStateChange,
  onArchive,
  archivedReportNames,
}: ArchiveDialogProps): JSX.Element {
  const [archiveName, setArchiveName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');

  // Clear input form when showing popup
  useEffect(() => {
    if (isOpen) {
      setArchiveName('');
    }
  }, [isOpen]);

  const archiveNameChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setArchiveName(e.target.value),
    [],
  );

  const archiveClickHandler = useCallback(async () => {
    setIsLoading(true);

    const archiveNameExists = archivedReportNames.includes(archiveName);

    if (archiveNameExists) {
      // setArchiveNameExists(
      //   `"${archiveName}" already exists as an archived report. Please select a new name.`,
      // );
      setDuplicateName(archiveName);
      setIsLoading(false);
    } else {
      const name = archiveName.trim();

      await postArchiveReport({
        backgroundTaskId: reportResultId,
        name,
      });

      setIsLoading(false);

      if (onArchive) {
        onArchive(reportResultId, name);
      }

      onStateChange(false);
      setDuplicateName('');
    }
  }, [
    archiveName,
    onArchive,
    onStateChange,
    reportResultId,
    archivedReportNames,
  ]);

  const cancelClickHandler = useCallback(() => {
    onStateChange(false);
    setDuplicateName('');
  }, [onStateChange]);

  return (
    <DialogWithTitle
      isOpen={isOpen}
      onStateChange={onStateChange}
      title="Please name the archived report."
      onCancel={cancelClickHandler}
    >
      <Input
        name="archiveName"
        value={archiveName}
        onChange={archiveNameChangeHandler}
        disabled={isLoading}
      />
      <StyledText as="p" color="warningRed">
        {duplicateName && duplicateName === archiveName && (
          <>
            <div>{duplicateName} already exists as an archived report.</div>
            <div>Please select a new name.</div>
          </>
        )}
      </StyledText>
      <Styled.ArchiveDialogButtonBar>
        <Button
          buttonType="primary"
          onClick={archiveClickHandler}
          disabled={isLoading || archiveName.trim() === ''}
        >
          Archive
        </Button>
        <Button buttonType="secondary" onClick={cancelClickHandler}>
          Cancel
        </Button>
      </Styled.ArchiveDialogButtonBar>
    </DialogWithTitle>
  );
}

export default memo(ArchiveDialog);
